<script setup lang="ts">
const props = defineProps<{
  isPending: boolean;
  disabled?: boolean;
  isFavorite: boolean;
  price?: number;
  carrierHoursText?: string;
}>();

const emit = defineEmits<{
  "add-to-cart": [];
  "toggle-favorite": [];
  /**
   * Tryb katalogowy: „Zapytaj o wycenę". Kontekst (produkt + WYBRANY wariant)
   * składa karta produktu — pasek sam znał tylko id produktu z adresu, więc
   * zapytanie szło o wariant, z którym weszło się na kartę.
   */
  "request-quote": [];
}>();

const { formatPrice } = useMoney();
// Tryb katalogowy: pasek prowadzi do wyceny, a ulubione (wishlist z backendu)
// są w nim wyłączone.
const { sellingEnabled, ctaLabel } = useCatalogOnly();
// „+ VAT 23%" przy cenie — ta sama zasada co na karcie (BrandConfig.priceTaxNote).
const priceTaxNote = useBrand().priceTaxNote ?? "";

const formattedPrice = computed(() =>
  typeof props.price === "number" && props.price > 0
    ? formatPrice(props.price)
    : "",
);
</script>

<template>
  <div class="sticky-bar">
    <div class="sticky-bar__inner">
      <p v-if="carrierHoursText" class="sticky-bar__notice">
        <ColorMarkedText :text="carrierHoursText" />
      </p>
      <div class="sticky-bar__row">
        <button
          v-if="sellingEnabled"
          type="button"
          class="sticky-bar__add-to-cart"
          :disabled="isPending || disabled"
          @click="emit('add-to-cart')"
        >
          <span class="sticky-bar__add-to-cart-label">
            <span v-if="formattedPrice" class="sticky-bar__price">{{
              formattedPrice
            }}</span>
            <span
              v-if="formattedPrice && priceTaxNote"
              class="sticky-bar__price-tax"
              >{{ priceTaxNote }}</span
            >
            <span
              v-if="formattedPrice"
              class="sticky-bar__separator"
              aria-hidden="true"
              >-</span
            >
            <span>Dodaj do koszyka</span>
          </span>
        </button>
        <!-- Przycisk, nie NuxtLink: `@click.prevent` nie powstrzymuje
             NuxtLinka przed nawigacją, więc modal wyceny otwierał się
             i natychmiast znikał razem ze zmianą strony. -->
        <button
          v-else
          type="button"
          class="sticky-bar__add-to-cart"
          @click="emit('request-quote')"
        >
          <span class="sticky-bar__add-to-cart-label">
            <span v-if="formattedPrice" class="sticky-bar__price">{{
              formattedPrice
            }}</span>
            <span
              v-if="formattedPrice && priceTaxNote"
              class="sticky-bar__price-tax"
              >{{ priceTaxNote }}</span
            >
            <span
              v-if="formattedPrice"
              class="sticky-bar__separator"
              aria-hidden="true"
              >-</span
            >
            <span>{{ ctaLabel }}</span>
          </span>
        </button>
        <button
          v-if="sellingEnabled"
          type="button"
          class="sticky-bar__wishlist"
          :class="{ 'is-favorite': isFavorite }"
          :aria-label="isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'"
          @click="emit('toggle-favorite')"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            :fill="isFavorite ? 'currentColor' : 'none'"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sticky-bar {
  display: none;
}

@media (max-width: 960px) {
  .sticky-bar {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: var(--z-sticky);
    background: var(--color-white);
    border-top: 1px solid var(--color-border);
  }

  .sticky-bar__inner {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }

  .sticky-bar__row {
    display: flex;
    gap: 8px;
  }

  .sticky-bar__notice {
    margin: 0;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: var(--font-weight-normal);
    color: var(--color-black);
    line-height: 1.2;
    text-align: center;
  }

  .sticky-bar__add-to-cart {
    flex: 1;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: var(--btn-primary-bg, var(--color-black));
    color: var(--btn-primary-text, var(--color-white));
    border: 1px solid var(--btn-primary-bg, var(--color-black));
    border-radius: var(--btn-pill-radius);
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: var(--font-weight-normal);
    letter-spacing: 0.04em;
    cursor: pointer;
    transition:
      background var(--transition-fast),
      color var(--transition-fast),
      border-color var(--transition-fast);
  }

  .sticky-bar__add-to-cart-label {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .sticky-bar__price {
    font-weight: var(--font-weight-bold);
  }

  .sticky-bar__separator {
    opacity: 0.7;
  }

  .sticky-bar__price-tax {
    font-size: 0.75rem;
    opacity: 0.8;
  }

  .sticky-bar__add-to-cart:hover {
    background: var(--color-white);
    color: var(--color-black);
  }

  .sticky-bar__add-to-cart:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sticky-bar__wishlist {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-white);
    border: 1px solid var(--color-border-md);
    border-radius: var(--btn-pill-radius);
    cursor: pointer;
    color: var(--color-black);
    transition:
      background var(--transition-fast),
      color var(--transition-fast),
      border-color var(--transition-fast);
  }

  .sticky-bar__wishlist:hover {
    background: var(--color-black);
    color: var(--color-white);
    border-color: var(--color-black);
  }

  .sticky-bar__wishlist.is-favorite {
    color: var(--color-sale);
  }
}
</style>
